import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAdmin } from '../AdminContext';
import AdminLayout from '../AdminLayout';
import {
  TrendingUp,
  Users,
  MousePointerClick,
  Globe2,
  Calendar,
  RefreshCw,
  Sparkles,
  ExternalLink,
  ChevronRight
} from 'lucide-react';

const LABELS = {
  instagram: { name: 'Instagram', short: '/ig', emoji: '📸', color: 'from-purple-500 to-pink-500' },
  facebook: { name: 'Facebook', short: '/fb', emoji: '👥', color: 'from-blue-600 to-blue-400' },
  tiktok: { name: 'TikTok', short: '/tt', emoji: '🎵', color: 'from-black to-gray-700' },
  reddit: { name: 'Reddit', short: '/rd', emoji: '🤖', color: 'from-orange-600 to-amber-500' },
  x: { name: 'X (Twitter)', short: '/x', emoji: '✖️', color: 'from-slate-800 to-slate-950' },
  threads: { name: 'Threads', short: '/th', emoji: '🧵', color: 'from-zinc-700 to-zinc-900' },
  youtube: { name: 'YouTube', short: '/yt', emoji: '▶️', color: 'from-red-600 to-rose-500' },
};

const PAGE_LABELS = {
  'quiz-love-language': { name: 'Love Language Quiz', path: '/quiz/love-language' },
  'quiz-attachment-style': { name: 'Attachment Style Quiz', path: '/quiz/attachment-style' },
  'never-forget-anything': { name: 'Calendar / Reminders', path: '/never-forget-anything' },
  'solve-fights-in-5-minutes': { name: 'Conflict Resolution', path: '/solve-fights-in-5-minutes' },
  'love-with-intentionality': { name: 'Brownie Points', path: '/love-with-intentionality' },
  'stay-connected-in-60-seconds': { name: 'Mood Sync', path: '/stay-connected-in-60-seconds' },
  blog: { name: 'Blog Index', path: '/blog' },
  home: { name: 'Home Landing Page', path: '/' },
};

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: 'Last 7 Days' },
  { id: '30d', label: 'Last 30 Days' },
  { id: '1y', label: 'Last 12 Months' },
  { id: 'all', label: 'All Time' },
];

const COUNTRY_NAMES = {
  US: 'United States', GB: 'United Kingdom', CA: 'Canada', AU: 'Australia',
  IN: 'India', DE: 'Germany', FR: 'France', ES: 'Spain', IT: 'Italy',
  NL: 'Netherlands', BR: 'Brazil', MX: 'Mexico', JP: 'Japan', KR: 'South Korea',
  SE: 'Sweden', NO: 'Norway', DK: 'Denmark', IE: 'Ireland', NZ: 'New Zealand',
  ZA: 'South Africa', SG: 'Singapore', AE: 'UAE', PH: 'Philippines',
  ID: 'Indonesia', PL: 'Poland', PT: 'Portugal', CH: 'Switzerland',
};

const flagEmoji = (code) => {
  if (!code || code.length !== 2) return '🌐';
  const A = 0x1f1e6;
  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => A + c.charCodeAt(0) - 65));
};

const monthLabel = (m) => {
  const [y, mo] = m.split('-');
  const d = new Date(Number(y), Number(mo) - 1, 1);
  return d.toLocaleString('en-US', { month: 'short', year: '2-digit' });
};

export default function AnalyticsView() {
  const { token, API } = useAdmin();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadStats = useCallback(async (tk, pd) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/track/stats`, {
        params: { period: pd },
        headers: { 'X-Admin-Passcode': tk },
      });
      setData(res.data);
      setError('');
    } catch (e) {
      setError('Could not load analytics. Please refresh or re-login.');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    if (token) {
      loadStats(token, period);
    }
  }, [token, period, loadStats]);

  const totalClicks = data?.stats ? Object.values(data.stats).reduce((a, b) => a + b, 0) : 0;
  const totalViews = data?.pages ? Object.values(data.pages).reduce((a, b) => a + (b.views || 0), 0) : 0;
  const totalDownloads = data?.pages ? Object.values(data.pages).reduce((a, b) => a + (b.downloads || 0), 0) : 0;

  const topSource = data?.stats
    ? Object.entries(data.stats).sort((a, b) => b[1] - a[1])[0]
    : null;

  return (
    <AdminLayout activeTab="analytics">
      <div className="space-y-6">
        
        {/* Header Bar & Period Controls */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white p-5 sm:p-6 rounded-3xl border border-slate-200/80 shadow-xs">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Traffic & Social Analytics
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              Real-time inbound short-links, page performance, and country engagement.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="bg-slate-100 p-1 rounded-2xl flex items-center gap-1">
              {PERIODS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPeriod(p.id)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                    period === p.id
                      ? 'bg-white text-pink-600 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <button
              onClick={() => loadStats(token, period)}
              disabled={loading}
              className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl transition-colors disabled:opacity-50"
              title="Refresh Data"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-2xl text-sm font-semibold flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => loadStats(token, period)} className="underline text-xs">Try again</button>
          </div>
        )}

        {/* KPI Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Card 1: Total Clicks */}
          <div className="bg-white p-5 sm:p-6 rounded-3xl border border-slate-200/80 shadow-xs relative overflow-hidden">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Social Clicks</span>
              <div className="w-8 h-8 rounded-xl bg-pink-50 text-pink-600 flex items-center justify-center">
                <MousePointerClick className="w-4 h-4" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-extrabold text-slate-900">
              {loading ? '...' : totalClicks.toLocaleString()}
            </p>
            <p className="text-xs text-slate-500 mt-1">From campaign short-links</p>
          </div>

          {/* Card 2: Total Page Views */}
          <div className="bg-white p-5 sm:p-6 rounded-3xl border border-slate-200/80 shadow-xs relative overflow-hidden">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Page Views</span>
              <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
                <Users className="w-4 h-4" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-extrabold text-slate-900">
              {loading ? '...' : totalViews.toLocaleString()}
            </p>
            <p className="text-xs text-slate-500 mt-1">Across landing & quiz pages</p>
          </div>

          {/* Card 3: Store Download Clicks */}
          <div className="bg-white p-5 sm:p-6 rounded-3xl border border-slate-200/80 shadow-xs relative overflow-hidden">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Download Intent</span>
              <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <TrendingUp className="w-4 h-4" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-extrabold text-slate-900">
              {loading ? '...' : totalDownloads.toLocaleString()}
            </p>
            <p className="text-xs text-slate-500 mt-1">App Store / Play Store clicks</p>
          </div>

          {/* Card 4: Top Channel */}
          <div className="bg-white p-5 sm:p-6 rounded-3xl border border-slate-200/80 shadow-xs relative overflow-hidden">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Top Channel</span>
              <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
            </div>
            <p className="text-xl sm:text-2xl font-extrabold text-slate-900 truncate">
              {topSource && topSource[1] > 0 ? (LABELS[topSource[0]]?.name || topSource[0]) : 'None yet'}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {topSource && topSource[1] > 0 ? `${topSource[1]} clicks recorded` : 'Awaiting traffic'}
            </p>
          </div>
        </div>

        {/* 12-Month Traffic Trends Graph */}
        {data?.monthly && data.monthly.length > 0 && (
          <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200/80 shadow-xs">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-slate-900">12-Month Growth Trends</h3>
                <p className="text-xs text-slate-500 mt-0.5">Historical traffic volume month-over-month</p>
              </div>
              <div className="flex items-center gap-4 text-xs font-semibold text-slate-600">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-pink-500" />
                  Social Clicks
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-slate-300" />
                  Page Views
                </span>
              </div>
            </div>

            <div className="grid grid-cols-6 sm:grid-cols-12 gap-2 items-end h-48 pt-4">
              {(() => {
                const maxVal = Math.max(
                  1,
                  ...data.monthly.map((m) => Math.max(m.clicks || 0, m.views || 0))
                );

                return data.monthly.map((m) => {
                  const clickHeight = Math.round(((m.clicks || 0) / maxVal) * 100);
                  const viewHeight = Math.round(((m.views || 0) / maxVal) * 100);

                  return (
                    <div key={m.month} className="flex flex-col items-center h-full justify-end group">
                      <div className="w-full flex items-end justify-center gap-1 h-36">
                        <div
                          style={{ height: `${Math.max(4, clickHeight)}%` }}
                          className="w-2.5 sm:w-3.5 bg-pink-500 rounded-t-md transition-all group-hover:bg-pink-600"
                          title={`Clicks: ${m.clicks || 0}`}
                        />
                        <div
                          style={{ height: `${Math.max(4, viewHeight)}%` }}
                          className="w-2.5 sm:w-3.5 bg-slate-200 rounded-t-md transition-all group-hover:bg-slate-300"
                          title={`Views: ${m.views || 0}`}
                        />
                      </div>
                      <span className="text-[10px] font-bold text-slate-400 mt-2 truncate max-w-[36px]">
                        {monthLabel(m.month)}
                      </span>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}

        {/* Detailed Grid: Channels & Pages */}
        <div className="grid lg:grid-cols-2 gap-6">
          
          {/* Social Channels Table */}
          <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xs overflow-hidden">
            <div className="p-5 sm:p-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-base sm:text-lg font-bold text-slate-900">Campaign Channels</h3>
                <p className="text-xs text-slate-500 mt-0.5">Short-links redirecting to app stores</p>
              </div>
              <span className="text-xs font-bold text-slate-400">
                {Object.keys(LABELS).length} Active
              </span>
            </div>

            <div className="divide-y divide-slate-100">
              {Object.entries(LABELS).map(([source, meta]) => {
                const count = data?.stats?.[source] || 0;
                const percentage = totalClicks > 0 ? Math.round((count / totalClicks) * 100) : 0;

                return (
                  <div key={source} className="p-4 sm:px-6 flex items-center justify-between hover:bg-slate-50/70 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{meta.emoji}</span>
                      <div>
                        <p className="text-sm font-bold text-slate-900">{meta.name}</p>
                        <a
                          href={`https://coupleinapp.com${meta.short}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-pink-600 hover:underline font-mono"
                        >
                          coupleinapp.com{meta.short}
                        </a>
                      </div>
                    </div>

                    <div className="text-right">
                      <p className="text-sm font-extrabold text-slate-900">{count.toLocaleString()} clicks</p>
                      <div className="w-20 bg-slate-100 rounded-full h-1.5 mt-1 overflow-hidden">
                        <div
                          style={{ width: `${percentage}%` }}
                          className="bg-pink-500 h-full rounded-full"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Landing Pages Table */}
          <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xs overflow-hidden">
            <div className="p-5 sm:p-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-base sm:text-lg font-bold text-slate-900">Landing Pages Performance</h3>
                <p className="text-xs text-slate-500 mt-0.5">Views and conversion to download</p>
              </div>
              <span className="text-xs font-bold text-slate-400">
                {Object.keys(PAGE_LABELS).length} Pages
              </span>
            </div>

            <div className="divide-y divide-slate-100">
              {Object.entries(PAGE_LABELS).map(([pageId, meta]) => {
                const pData = data?.pages?.[pageId] || { views: 0, downloads: 0 };
                const conversion = pData.views > 0 ? Math.round((pData.downloads / pData.views) * 100) : 0;

                return (
                  <div key={pageId} className="p-4 sm:px-6 flex items-center justify-between hover:bg-slate-50/70 transition-colors">
                    <div>
                      <p className="text-sm font-bold text-slate-900">{meta.name}</p>
                      <a
                        href={meta.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-slate-400 hover:text-pink-600 transition-colors font-mono"
                      >
                        {meta.path}
                      </a>
                    </div>

                    <div className="text-right">
                      <div className="flex items-center gap-3 justify-end">
                        <span className="text-xs text-slate-500">
                          <strong className="text-slate-900">{pData.views.toLocaleString()}</strong> views
                        </span>
                        <span className="text-xs bg-pink-50 text-pink-600 font-bold px-2 py-0.5 rounded-md">
                          {conversion}% CVR
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Geographic Distribution */}
        {data?.regions && Object.keys(data.regions).length > 0 && (
          <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xs p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-base sm:text-lg font-bold text-slate-900">Geographic Visitor Breakdown</h3>
                <p className="text-xs text-slate-500 mt-0.5">Origin of engagement via edge CDN resolution</p>
              </div>
              <Globe2 className="w-5 h-5 text-slate-400" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {Object.entries(data.regions)
                .sort((a, b) => ((b[1].clicks || 0) + (b[1].views || 0)) - ((a[1].clicks || 0) + (a[1].views || 0)))
                .slice(0, 12)
                .map(([countryCode, stats]) => (
                  <div key={countryCode} className="bg-slate-50 rounded-2xl p-3.5 border border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{flagEmoji(countryCode)}</span>
                      <span className="text-xs font-bold text-slate-800 truncate max-w-[90px]">
                        {COUNTRY_NAMES[countryCode] || countryCode}
                      </span>
                    </div>
                    <span className="text-xs font-extrabold text-pink-600">
                      {(stats.clicks || 0) + (stats.views || 0)}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
